/**
 * Rules text normalisation and hashing (research 03 §1.4; owner decision Q43: curated oracle text per
 * card with text hashes that flag catalogue changes for review).
 *
 * `text_rich` (a tiny HTML subset: `<p>`, `<br />`, `<ul><li>`) is the source of ability boundaries;
 * `text_plain` loses its line breaks and is only a fallback. The **oracle** is the rich text as plain
 * lines (`• ` for list items), entities decoded; `textHash` is `sha256:` + its hex SHA-256.
 */
import { sha256Hex } from '@play/engine';

const ENTITIES: Readonly<Record<string, string>> = { gt: '>', lt: '<', amp: '&', quot: '"', apos: "'", nbsp: ' ' };

/** Decodes the HTML entities Riftcodex uses (named, decimal and hex). */
export function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, code: string) => {
    if (code.startsWith('#')) {
      const n = code[1]?.toLowerCase() === 'x' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      return Number.isFinite(n) ? String.fromCodePoint(n) : match;
    }
    return ENTITIES[code.toLowerCase()] ?? match;
  });
}

/** A paragraph or list item of rules text. */
export interface RulesBlock {
  kind: 'p' | 'li';
  text: string;
}

/** Splits rules text into paragraphs and list items (the same split runebound.gg renders). */
export function rulesBlocks(rich: string | null | undefined, plain: string | null | undefined): RulesBlock[] {
  if (rich) {
    const blocks: RulesBlock[] = [];
    const re = /<(p|li)\b[^>]*>([\s\S]*?)<\/\1>/gi;
    for (const match of rich.matchAll(re)) {
      const kind = (match[1] ?? 'p').toLowerCase() === 'li' ? 'li' : 'p';
      for (const line of (match[2] ?? '').split(/<br\s*\/?>/i)) {
        const text = decodeEntities(line.replace(/<[^>]+>/g, '')).trim();
        if (text) blocks.push({ kind, text });
      }
    }
    if (blocks.length > 0) return blocks;
  }
  if (plain) {
    // text_plain joins sentences after ")" without a space: restore the break before the next ability.
    return decodeEntities(plain)
      .replace(/\)(?=[A-Z[])/g, ')\n')
      .split(/\n+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((text) => ({ kind: 'p' as const, text }));
  }
  return [];
}

/** The curated oracle text: one line per block, list items prefixed with `• `. Runes have `[NO TEXT]`. */
export function oracleText(rich: string | null | undefined, plain: string | null | undefined): string {
  return rulesBlocks(rich, plain)
    .map((b) => (b.kind === 'li' ? `• ${b.text}` : b.text))
    .join('\n');
}

/** `sha256:<hex>` of the oracle text. */
export function textHash(oracle: string): string {
  return `sha256:${sha256Hex(oracle)}`;
}

/** Whether a card has rules text worth showing (runes print `[NO TEXT]`). */
export function hasRulesText(oracle: string): boolean {
  const t = oracle.trim();
  return t.length > 0 && t !== '[NO TEXT]';
}

/** `[Unique]`: at most one copy of this name in a deck (CR 825.3.a). */
export function isUniqueText(oracle: string, keywords: readonly string[]): boolean {
  return /\[Unique\]/i.test(oracle) || keywords.some((k) => k.toLowerCase() === 'unique');
}

/**
 * Card text that lifts the copy limit, e.g. Spiderling's "Your deck can have any number of cards named
 * Spiderling" (CR 002: card text overrides the rules). Returns the named card, or `null`.
 */
export function unlimitedCopiesName(oracle: string): string | null {
  const match = /Your deck can have any number of cards named ([^.\n]+)\./i.exec(oracle);
  return match?.[1]?.trim() ?? null;
}
